<template>
  <div class="gc-bom-bulk-update-step-apply">
    <!-- ── Post-apply outcome report ────────────────────────────────────────── -->
    <GcBomBulkUpdateOutcomeReport
      v-if="applyResult"
      :apply-result="applyResult"
      :matched-bom-list="matchedBomList"
      :excluded-bom-ids="excludedBomIds"
      :lines-changed="linesChanged"
      :lines-added="linesAdded"
      @back-to-list="$emit('back-to-list', $event)"
    />

    <!-- ── Pre-apply form ─────────────────────────────────────────────────────── -->
    <template v-else>
      <!-- Top warning banner -->
      <div class="apl-top-banner">
        ⚠ You are about to apply changes to
        <strong
          >{{ bomsToUpdate }} BOM{{ bomsToUpdate !== 1 ? "s" : "" }}</strong
        >. BOM cost will be automatically recalculated for each updated BOM.
        This action cannot be undone.
      </div>

      <div class="apl-body-layout">
        <!-- ── Main column ─────────────────────────────────────────────────────── -->
        <div class="apl-main">
          <!-- Stale diff banner — inline in Apply step (TC-096) -->
          <GcBomBulkUpdateStaleDiffBanner
            v-if="staleDiffWarningActive && diffComputedAt"
            :stale-boms="staleBoms"
            :deleted-boms="deletedBoms"
            :diff-computed-at="diffComputedAt"
            :is-read-only="isReadOnly"
            @acknowledge="$emit('stale-diff-acknowledge', $event)"
            @recompute="$emit('stale-diff-recompute')"
          />

          <!-- Summary metric cards -->
          <div class="apl-metrics">
            <div class="apl-metric-card">
              <span class="apl-metric-label">BOMs to update</span>
              <span class="apl-metric-val apl-val-blue">{{
                bomsToUpdate
              }}</span>
            </div>
            <div class="apl-metric-card">
              <span class="apl-metric-label">Lines changed</span>
              <span class="apl-metric-val apl-val-amber">{{
                linesChanged
              }}</span>
            </div>
            <div class="apl-metric-card">
              <span class="apl-metric-label">Lines added</span>
              <span class="apl-metric-val apl-val-green">{{ linesAdded }}</span>
            </div>
            <div class="apl-metric-card">
              <span class="apl-metric-label">BOMs excluded</span>
              <span
                :class="[
                  'apl-metric-val',
                  bomsExcluded > 0 ? 'apl-val-red' : '',
                ]"
                >{{ bomsExcluded }}</span
              >
            </div>
          </div>

          <!-- Update reference — optional notes for the apply operation -->
          <!-- <div class="apl-section-card">
          <div class="apl-section-header">
            <span class="apl-section-title">Update reference</span>
          </div>
          <div class="apl-section-body">
            <label class="apl-field-label"
              >Change reason / notes (optional)</label
            >
            <textarea
              v-model="noteText"
              class="apl-notes-input"
              placeholder="e.g. Q1 material spec revision"
              :disabled="isReadOnly"
              maxlength="500"
              rows="3"
            />
          </div>
        </div> -->

          <!-- Confirm box -->
          <div
            :class="[
              'apl-confirm-box',
              { 'apl-confirm-box-active': confirmReady },
            ]"
          >
            <div class="apl-confirm-instruction">
              Type <strong>CONFIRM</strong> to apply changes to
              <strong
                >{{ bomsToUpdate }} BOM{{
                  bomsToUpdate !== 1 ? "s" : ""
                }}</strong
              >.
            </div>
            <div v-if="staleDiffWarningActive" class="apl-stale-guard-note">
              ⛔ Resolve the stale diff warning above before applying.
            </div>
            <div class="apl-confirm-actions">
              <div class="apl-confirm-input-wrap">
                <input
                  v-model="confirmText"
                  :class="[
                    'apl-confirm-input',
                    'apl-confirm-input--' + confirmState,
                  ]"
                  placeholder="CONFIRM"
                  :disabled="isReadOnly"
                  autocomplete="off"
                  spellcheck="false"
                />
                <span v-if="confirmState === 'wrong'" class="apl-confirm-hint">
                  Must match exactly: CONFIRM (case-sensitive)
                </span>
              </div>
              <DxButton
                text="Apply Bulk Update"
                type="default"
                styling-mode="contained"
                class="apl-apply-btn"
                :disabled="!canApply || isReadOnly"
                @click="onApplyClick"
              />
              <DxButton
                text="← Back to preview"
                type="normal"
                styling-mode="outlined"
                @click="$emit('go-back')"
              />
            </div>
          </div>
        </div>

        <!-- ── Sidebar ─────────────────────────────────────────────────────────── -->
        <div class="apl-sidebar">
          <div class="apl-sidebar-card">
            <div class="apl-sidebar-header">
              <span class="apl-sidebar-title">Apply summary</span>
            </div>
            <div class="apl-sidebar-body">
              <div class="apl-sidebar-row">
                <span class="apl-sidebar-lbl">BOMs to update</span>
                <span class="apl-sidebar-val apl-val-blue">{{
                  bomsToUpdate
                }}</span>
              </div>
              <div class="apl-sidebar-row">
                <span class="apl-sidebar-lbl">BOMs excluded</span>
                <span
                  :class="[
                    'apl-sidebar-val',
                    bomsExcluded > 0 ? 'apl-val-red' : '',
                  ]"
                  >{{ bomsExcluded }}</span
                >
              </div>
              <div class="apl-sidebar-row">
                <span class="apl-sidebar-lbl">Lines changed</span>
                <span class="apl-sidebar-val apl-val-amber">{{
                  linesChanged
                }}</span>
              </div>
              <div class="apl-sidebar-row">
                <span class="apl-sidebar-lbl">Lines added</span>
                <span class="apl-sidebar-val apl-val-green">{{
                  linesAdded
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import DxButton from "devextreme-vue/button";
import GcBomBulkUpdateStaleDiffBanner from "./gc-bom-bulk-update-stale-diff-banner.vue";
import GcBomBulkUpdateOutcomeReport from "./gc-bom-bulk-update-outcome-report.vue";

export default {
  name: "GcBomBulkUpdateStepApply",
  components: {
    DxButton,
    GcBomBulkUpdateStaleDiffBanner,
    GcBomBulkUpdateOutcomeReport,
  },
  props: {
    active: { type: Boolean, default: false },
    // Summary counts — bomsToUpdate and bomsExcluded wired now;
    // linesChanged and linesAdded wired in next task from diff snapshot
    bomsToUpdate: { type: Number, default: 0 },
    bomsExcluded: { type: Number, default: 0 },
    linesChanged: { type: Number, default: 0 },
    linesAdded: { type: Number, default: 0 },
    // Stale diff
    staleDiffWarningActive: { type: Boolean, default: false },
    staleBoms: { type: Array, default: () => [] },
    deletedBoms: { type: Array, default: () => [] },
    diffComputedAt: { type: String, default: null },
    isReadOnly: { type: Boolean, default: false },
    applyResult: { type: Object, default: null },
    // Outcome report data
    matchedBomList: { type: Array, default: () => [] },
    excludedBomIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      confirmText: "",
      noteText: "",
    };
  },
  computed: {
    confirmState() {
      if (!this.confirmText) return "empty";
      return this.confirmText === "CONFIRM" ? "correct" : "wrong";
    },
    confirmReady() {
      return this.confirmState === "correct";
    },
    canApply() {
      return this.confirmReady && !this.staleDiffWarningActive;
    },
  },
  watch: {
    active(val) {
      if (!val) {
        this.confirmText = "";
      }
    },
  },
  methods: {
    onApplyClick() {
      if (!this.canApply) return;
      this.$emit("apply", { noteText: this.noteText });
    },
    formatTimestamp(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      return d.toLocaleString("en-AU", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
  },
};
</script>

<style lang="scss">
.gc-bom-bulk-update-step-apply {
  display: flex;
  flex-direction: column;
  gap: 16px;

  // ── Top warning banner ────────────────────────────────────────────────────────
  .apl-top-banner {
    padding: 12px 16px;
    background: #fff8f0;
    border: 1px solid #ffd6a5;
    border-radius: 6px;
    font-size: 13px;
    color: #7c4b00;
  }

  // ── Body layout — main + sidebar ──────────────────────────────────────────────
  .apl-body-layout {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .apl-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .apl-sidebar {
    width: 240px;
    flex-shrink: 0;
  }

  // ── Metric cards ──────────────────────────────────────────────────────────────
  .apl-metrics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }

  .apl-metric-card {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
  }

  .apl-metric-label {
    font-size: 11px;
    font-weight: 500;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .apl-metric-val {
    font-size: 26px;
    font-weight: 700;
    color: #101828;
    line-height: 1;
  }

  // ── Colour values ─────────────────────────────────────────────────────────────
  .apl-val-blue {
    color: #0369a1;
  }
  .apl-val-amber {
    color: #92400e;
  }
  .apl-val-green {
    color: #065f46;
  }
  .apl-val-red {
    color: #9b1c1c;
  }

  // ── Section card (Update reference) ──────────────────────────────────────────
  .apl-section-card {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    overflow: hidden;
  }

  .apl-section-header {
    padding: 10px 14px;
    border-bottom: 1px solid #dee2e6;
    background: #f8f9fa;
  }

  .apl-section-title {
    font-size: 13px;
    font-weight: 600;
    color: #344054;
  }

  .apl-section-body {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .apl-field-label {
    font-size: 12px;
    font-weight: 500;
    color: #344054;
  }

  .apl-notes-input {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 13px;
    color: #101828;
    resize: vertical;
    font-family: inherit;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: #03a9f4;
      box-shadow: 0 0 0 2px rgba(3, 169, 244, 0.15);
    }

    &:disabled {
      background: #f8f9fa;
      color: #6c757d;
    }
  }

  // ── Confirm box ───────────────────────────────────────────────────────────────
  .apl-confirm-box {
    padding: 16px;
    background: #fff5f5;
    border: 1px solid #f5c6cb;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    transition: background 0.15s, border-color 0.15s;

    &.apl-confirm-box-active {
      background: #f0fdf4;
      border-color: #86efac;
    }
  }

  .apl-confirm-instruction {
    font-size: 13px;
    font-weight: 500;
    color: #9b1c1c;

    .apl-confirm-box-active & {
      color: #065f46;
    }
  }

  .apl-stale-guard-note {
    font-size: 12px;
    color: #9b1c1c;
    background: #fff3f3;
    border: 1px solid #f5c6cb;
    border-radius: 4px;
    padding: 8px 12px;
  }

  .apl-confirm-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .apl-confirm-input-wrap {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .apl-confirm-input {
    width: 160px;
    padding: 7px 10px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 14px;
    font-family: monospace;
    letter-spacing: 0.08em;
    color: #101828;
    background: #fff;

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(3, 169, 244, 0.15);
    }

    &--empty {
      border-color: #ced4da;
    }

    &--wrong {
      border-color: #dc3545;
      background: #fff8f8;

      &:focus {
        box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.15);
      }
    }

    &--correct {
      border-color: #17b26a;
      background: #f0fdf4;

      &:focus {
        box-shadow: 0 0 0 2px rgba(23, 178, 106, 0.15);
      }
    }

    &:disabled {
      background: #f8f9fa;
      color: #6c757d;
    }
  }

  .apl-confirm-hint {
    font-size: 11px;
    color: #dc3545;
  }

  // ── Sidebar card ──────────────────────────────────────────────────────────────
  .apl-sidebar-card {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
  }

  .apl-sidebar-header {
    padding: 10px 14px;
    border-bottom: 1px solid #dee2e6;
    background: #f8f9fa;
    border-radius: 6px 6px 0 0;
  }

  .apl-sidebar-title {
    font-size: 12px;
    font-weight: 600;
    color: #344054;
  }

  .apl-sidebar-body {
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .apl-sidebar-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .apl-sidebar-lbl {
    font-size: 12px;
    color: #495057;
  }

  .apl-sidebar-val {
    font-size: 14px;
    font-weight: 700;
    color: #101828;
  }
}
</style>
